import styled from 'styled-components'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { DASHBOARD, VIEWS } from './remedialsQuery'
import { ACCENT, ACCENT_SOFT, BORDER, INK, MUTED } from './ui'

const RAIL_WIDTH = 64
const OPEN_WIDTH = 248
const DEFAULT_TOP = 'calc(var(--passport-bar-bottom, 0px) + 12px)'

const ICONS = {
  [DASHBOARD]: DashboardOutlinedIcon,
  mine: PersonOutlineIcon,
  all_open: FormatListBulletedIcon,
  overdue: WarningAmberIcon,
  due_this_week: EventOutlinedIcon,
  needs_decision: HelpOutlineIcon,
  task_in_progress: PendingActionsOutlinedIcon,
  awaiting_review: RateReviewOutlinedIcon,
  resolved: CheckCircleOutlineIcon,
  dismissed: DoNotDisturbOnOutlinedIcon,
}

const ALERTS = { overdue: 'critical', needs_decision: 'high' }

const GROUPS = [
  [DASHBOARD],
  ['mine', 'all_open'],
  ['overdue', 'due_this_week'],
  ['needs_decision', 'task_in_progress', 'awaiting_review'],
  ['resolved', 'dismissed'],
]

const ITEMS = [{ key: DASHBOARD, label: 'Dashboard', countKey: null }, ...VIEWS]
const itemFor = key => ITEMS.find(item => item.key === key)

const ViewSidebar = ({ view, counts, countsReady, statsError = false, onRetry, onSelect, compact = false, mode = 'rail', stickyTop = DEFAULT_TOP, hideOnMobile = false }) => {
  const options = ITEMS.map(item => ({
    value: item.key,
    label: item.countKey && countsReady ? `${item.label} (${counts[item.countKey]})` : item.label,
  }))

  if (mode === 'inline') {
    return (
      <Inline>
        <SearchableSelect
          value={view}
          onChange={value => value && onSelect(value)}
          options={options}
          searchable={false}
          ariaLabel='Choose which remedials to show'
        />
      </Inline>
    )
  }

  if (mode === 'picker') {
    return (
      <Compact $always={compact} $hideOnMobile={hideOnMobile}>
        <CompactLabel>Show</CompactLabel>
        <SearchableSelect
          value={view}
          onChange={value => value && onSelect(value)}
          options={options}
          searchable={false}
          ariaLabel='Choose which remedials to show'
        />
      </Compact>
    )
  }

  if (compact) return null

  return (
    <RailSlot>
      <Rail aria-label='Remedial views' style={{ top: stickyTop, maxHeight: `calc(100vh - ${stickyTop} - 12px)` }}>
        {GROUPS.map((group, index) => (
          <Group key={group.join('-')} $first={index === 0}>
            {group.map(key => {
              const item = itemFor(key)
              const Icon = ICONS[key]
              const active = view === key
              const count = item.countKey ? counts[item.countKey] : null
              const alert = ALERTS[key] && countsReady && count > 0 ? RAG[ALERTS[key]] : null
              return (
                <Item
                  key={key}
                  type='button'
                  $active={active}
                  aria-current={active ? 'page' : undefined}
                  title={item.label}
                  onClick={() => onSelect(key)}
                >
                  <IconWrap>
                    <Icon aria-hidden='true' />
                    {alert && (
                      <Badge aria-hidden='true' style={{ color: '#fff', background: alert.fg }}>
                        {count > 99 ? '99+' : count}
                      </Badge>
                    )}
                  </IconWrap>
                  <Label>{item.label}</Label>
                  {item.countKey && !statsError && (countsReady
                    ? <Count style={alert ? { color: alert.fg, background: alert.bg } : undefined}>{count}</Count>
                    : <Fade><SkeletonLine $w='22px' $h={14} /></Fade>)}
                </Item>
              )
            })}
          </Group>
        ))}
        {statsError && (
          <Group>
            <Item type='button' onClick={onRetry} title='We couldn’t load the totals. Try again'>
              <IconWrap><RefreshIcon aria-hidden='true' style={{ color: RAG.critical.fg }} /></IconWrap>
              <Label>Totals did not load. Try again</Label>
            </Item>
          </Group>
        )}
      </Rail>
    </RailSlot>
  )
}

export default ViewSidebar

const Label = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`

const Fade = styled.span`
  display: inline-flex;
`

const Count = styled.span`
  flex-shrink: 0;
  min-width: 26px;
  padding: 2px 8px;
  border-radius: 999px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: ${MUTED};
  background: #f4f4f5;
`

const Badge = styled.span`
  position: absolute;
  top: -7px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border: 2px solid #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  box-sizing: border-box;
`

const Rail = styled.nav`
  position: sticky;
  z-index: 5;
  display: flex;
  flex-direction: column;
  width: ${RAIL_WIDTH}px;
  padding: 6px 0;
  overflow: hidden;
  white-space: nowrap;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  transition: width 0.18s ease, box-shadow 0.18s ease;

  ${Label}, ${Count}, ${Fade} {
    opacity: 0;
    transition: opacity 0.12s ease;
  }

  &:hover, &:focus-within {
    width: ${OPEN_WIDTH}px;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(12, 18, 41, 0.08);
  }

  &:hover ${Label}, &:hover ${Count}, &:hover ${Fade},
  &:focus-within ${Label}, &:focus-within ${Count}, &:focus-within ${Fade} {
    opacity: 1;
  }

  &:hover ${Badge}, &:focus-within ${Badge} {
    opacity: 0;
  }
`

const RailSlot = styled.div`
  position: relative;
  width: ${RAIL_WIDTH}px;
  align-self: stretch;
  transition: width 0.18s ease;
  &:has(${Rail}:hover), &:has(${Rail}:focus-within) { width: ${OPEN_WIDTH}px; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
  @media (max-width: 768px) { display: none; }
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 8px;
  border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${BORDER}`)};
`

const Item = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 0 10px 0 12px;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? ACCENT_SOFT : 'transparent')};
  color: ${INK};
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover { background: ${({ $active }) => ($active ? ACCENT_SOFT : '#f4f4f5')}; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: -2px; }
  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 0 2px 2px 0;
    background: ${({ $active }) => ($active ? ACCENT : 'transparent')};
  }
  svg { font-size: 22px; color: ${({ $active }) => ($active ? INK : MUTED)}; }
`

const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`


const Compact = styled.div`
  display: ${({ $always }) => ($always ? 'flex' : 'none')};
  align-items: center;
  gap: 10px;
  & > button { flex: 1; min-height: 44px; font-size: 15px; }
  @media (max-width: 768px) { display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'flex')}; }
`

const Inline = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    & > button { flex: 1; min-width: 0; min-height: 40px; font-size: 15px; }
  }
`

const CompactLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`
